import { defineStore, acceptHMRUpdate } from 'pinia';
import moment from '@/utils/moment-wrapper';
import values from 'lodash/values';
import keyBy from 'lodash/keyBy';
import pick from 'lodash/pick';
import { PerSubscriptionUserStorage } from '@/utils/storage';
import {
    fetchDashboards,
    fetchDashboard,
    addSharedDashboard,
    deleteDashboard,
    createDashboard,
    updateDashboard,
    fetchDashboardPermissions,
    shareDashboardWithUsers,
    updateDashboardSubPermissions,
    updateDashboardUserPermissions,
    unshareUserFromDashboard,
} from '@/vuex/modules/dashboards/dashboards.utils';
import { v4 as uuid } from 'uuid';
import widgetDomSettings from '@/dashboards-v2/widget-maps/widget-dom-settings.map';
import { validateWidgetAccess } from '@/dashboards-v2/helpers/validate-widget-access';

import { useSubscriptionStore } from '@/stores/subscription.store';

let loadAllPromise: Promise<unknown> | null = null;

interface DashboardHistoryItem {
    id: string;
    timestamp: number;
}

function getInitialState () {
    return {
        error: null as unknown,
        fetching: false,
        fetchingByQuery: false,
        fetchingPermissions: false,
        fetched: false,
        fetchedByQuery: false,
        map: {} as Record<string, unknown>,
        dashboardFilterMap: undefined as Record<string, unknown> | undefined,
        dashboardHistory: [] as DashboardHistoryItem[],
        creating: false,
        pesDrilldown: {
            account: null as unknown,
            appId: 0,
            opened: false,
            score: 0,
            segmentId: 0,
            timeSeries: null as unknown,
            type: 0,
            widgetId: null as unknown,
        },
    };
}

export const useDashboardsStore = defineStore('dashboards', {
    state: getInitialState,
    actions: {
        setMapAtKey ({ dashboard }: { dashboard: Record<string, unknown> }) {
            this.map[dashboard.id as string] = dashboard;
        },
        updateMap ({ values: vals }: { values: unknown[] }) {
            const dashMap = keyBy(vals as Record<string, unknown>[], 'id');
            this.map = { ...this.map, ...dashMap };
        },
        deleteMapAtKey ({ id }: { id: string }) {
            delete this.map[id];
        },
        setError ({ error }: { error: unknown }) {
            this.error = error;
        },
        setFetching ({ fetching }: { fetching: boolean }) {
            this.fetching = fetching;
        },
        setFetchingByQuery ({ fetching }: { fetching: boolean }) {
            this.fetchingByQuery = fetching;
        },
        setFetchingPermissions ({ fetching }: { fetching: boolean }) {
            this.fetchingPermissions = fetching;
        },
        setFetched ({ fetched }: { fetched: boolean }) {
            this.fetched = fetched;
        },
        setFetchedByQuery ({ fetched }: { fetched: boolean }) {
            this.fetchedByQuery = fetched;
        },
        setDashboardProperty ({ id, propertyKey, propertyValue }: { id: string; propertyKey: string; propertyValue: unknown }) {
            (this.map[id] as Record<string, unknown>)[propertyKey] = propertyValue;
        },
        updateDashboardProperties ({ dashboard }: { dashboard: Record<string, unknown> }) {
            Object.keys(dashboard).forEach((key) => {
                (this.map[dashboard.id as string] as Record<string, unknown>)[key] = dashboard[key];
            });
        },
        setPesDrilldown (options: Record<string, unknown>) {
            for (const optionKey in options) {
                if ((this.pesDrilldown as Record<string, unknown>)[optionKey] !== undefined) {
                    (this.pesDrilldown as Record<string, unknown>)[optionKey] = options[optionKey];
                }
            }
        },
        setDashboardModules ({ id, modules }: { id: string; modules: unknown[] }) {
            ((this.map[id] as Record<string, unknown>).options as Record<string, unknown>).modules = modules;
        },
        setDashboardWidget ({ dashboardId, widgetId, widget }: { dashboardId: string; widgetId: string; widget: unknown }) {
            const { widgets } = this.map[dashboardId] as { widgets: unknown[] };
            const index = widgets.findIndex((currWidget) => (currWidget as Record<string, unknown>).id === widgetId);
            widgets.splice(index, 1, widget);
        },
        setDashboardFilterMap (dashboardFilterMap: Record<string, unknown>) {
            this.dashboardFilterMap = dashboardFilterMap;
        },
        setDashboardFilterMapAtKey ({ dashboardId, filterSettings }: { dashboardId: string; filterSettings: unknown }) {
            this.dashboardFilterMap = {
                ...this.dashboardFilterMap,
                [dashboardId]: filterSettings,
            };
        },
        addDashboardWidgets ({ id, newWidgets, position = 0 }: { id: string; newWidgets: unknown[]; position?: number }) {
            const { widgets } = this.map[id] as { widgets: unknown[] };
            newWidgets.forEach((widget) => {
                if ((widget as Record<string, unknown>).isDuplicate) {
                    widgets.splice(position + 1, 0, widget);
                } else {
                    widgets.unshift(widget);
                }
            });
        },
        removeDashboardWidget ({ dashboardId, widgetId }: { dashboardId: string; widgetId: string }) {
            const { widgets } = this.map[dashboardId] as { widgets: unknown[] };
            const index = widgets.findIndex((currWidget) => (currWidget as Record<string, unknown>).id === widgetId);
            widgets.splice(index, 1);
        },
        setCreating ({ creating }: { creating: boolean }) {
            this.creating = creating;
        },
        async loadAll ({ reFetch = false } = {}) {
            if (loadAllPromise && !reFetch) {
                return loadAllPromise;
            }

            this.setFetching({ fetching: true });
            try {
                loadAllPromise = fetchDashboards({ include: 'createdByMe,sharedWithMe' });
                const dashboards = ((await loadAllPromise) as unknown[]).filter((item: unknown) => !((item as Record<string, unknown>)?.options as Record<string, unknown>)?.isExecutive);

                this.setFetching({ fetching: false });
                this.updateMap({ values: dashboards });
                this.setFetched({ fetched: true });
            } catch (error) {
                this.setFetching({ fetching: false });
                this.setError({ error });
                loadAllPromise = null;
                throw error;
            }
        },
        async loadByQuery ({ query = 'all' } = {}) {
            this.setFetchingByQuery({ fetching: true });
            try {
                const dashboards = ((await fetchDashboards({ include: query })) as unknown[]).filter(
                    (item: unknown) => !((item as Record<string, unknown>)?.options as Record<string, unknown>)?.isExecutive,
                );

                this.setFetchingByQuery({ fetching: false });
                await loadAllPromise;
                this.updateMap({ values: dashboards });
                this.setFetchedByQuery({ fetched: true });
            } catch (error) {
                this.setFetchingByQuery({ fetching: false });
                this.setError({ error });
                throw error;
            }
        },
        async loadOne ({ reFetch = false, id }: { reFetch?: boolean; id: string } = { id: '' }) {
            if (this.map[id] && !reFetch) {
                return;
            }

            this.setFetching({ fetching: true });
            try {
                const dashboard = await fetchDashboard(id);
                this.setFetching({ fetching: false });
                this.setMapAtKey({ dashboard: dashboard as Record<string, unknown> });
            } catch (error) {
                this.setFetching({ fetching: false });
                this.setError({ error });
                throw error;
            }
        },
        async loadDashboardFilterMap () {
            if (this.dashboardFilterMap) return;
            const dashboardFilterMap = ((await PerSubscriptionUserStorage.get('dashboardFilterMap')) || {}) as unknown as Record<string, unknown>;

            this.setDashboardFilterMap((dashboardFilterMap as Record<string, unknown>)?.value as Record<string, unknown> || {});
        },
        async addDashboard (dashboard: Record<string, unknown>) {
            await loadAllPromise;
            this.setMapAtKey({ dashboard });
        },
        async createV2Dashboard (dashboard: Record<string, unknown>) {
            if (this.creating) return;

            this.setCreating({ creating: true });
            const newDashboard = await createDashboard(dashboard) as Record<string, unknown>;
            newDashboard.isNew = true;
            await loadAllPromise;
            this.setMapAtKey({ dashboard: newDashboard });
            this.setCreating({ creating: false });

            return newDashboard;
        },
        async deleteDashboard (id: string) {
            await deleteDashboard(id);
            await loadAllPromise;
            this.deleteMapAtKey({ id });
        },
        async fetchDashboardPermissions ({ reFetch = false, id }: { reFetch?: boolean; id: string }) {
            if ((this.map[id] as Record<string, unknown>)?.subscriptionSharing && !reFetch) {
                return;
            }

            this.setFetchingPermissions({ fetching: true });
            try {
                const permissionInfo = await fetchDashboardPermissions(id) as Record<string, unknown>;
                await loadAllPromise;
                this.setDashboardProperty({ id, propertyKey: 'subscriptionSharing', propertyValue: permissionInfo.subscriptionSharing });
                if (permissionInfo.userSharing) {
                    this.setDashboardProperty({ id, propertyKey: 'userSharing', propertyValue: permissionInfo.userSharing });
                }
                this.setFetchingPermissions({ fetching: false });
            } catch (error) {
                this.setFetchingPermissions({ fetching: false });
                this.setError({ error });
                throw error;
            }
        },
        async shareDashboardWithUsers ({ id, params }: { id: string; params: unknown }) {
            const permissionInfo = await shareDashboardWithUsers(id, params) as Record<string, unknown>;
            await loadAllPromise;
            this.updateDashboardProperties({
                dashboard: { id, isShared: permissionInfo.isShared, userSharing: permissionInfo.userSharing },
            });
        },
        async updateDashboardUserPermissions ({ dashboardId, userId, permission }: { dashboardId: string; userId: string; permission: string }) {
            const permissionInfo = await updateDashboardUserPermissions(dashboardId, userId, permission) as Record<string, unknown>;
            await loadAllPromise;
            this.updateDashboardProperties({
                dashboard: { id: dashboardId, userSharing: permissionInfo.userSharing, userDetails: permissionInfo.userDetails },
            });
        },
        async updateDashboardSubPermissions ({ id, permission }: { id: string; permission: string }) {
            const permissionInfo = await updateDashboardSubPermissions(id, permission) as Record<string, unknown>;
            await loadAllPromise;
            this.updateDashboardProperties({
                dashboard: { id, isShared: permissionInfo.isShared, subscriptionSharing: permissionInfo.subscriptionSharing, userDetails: permissionInfo.userDetails },
            });
        },
        async updateDashboardFilters ({ dashboardId, filterSettings }: { dashboardId: string; filterSettings: unknown }) {
            this.setDashboardFilterMapAtKey({ dashboardId, filterSettings });
            await PerSubscriptionUserStorage.set('dashboardFilterMap', this.dashboardFilterMap);
        },
        async unshareUserFromDashboard ({ dashboardId, userId }: { dashboardId: string; userId: string }) {
            const response = await unshareUserFromDashboard(dashboardId, userId) as { data: Record<string, unknown> };
            await loadAllPromise;
            this.setMapAtKey({ dashboard: response.data });
        },
        async addShared (id: string) {
            const dashboard = await addSharedDashboard(id) as Record<string, unknown>;
            await loadAllPromise;
            this.setMapAtKey({ dashboard });
        },
        async removeShared (id: string) {
            await deleteDashboard(id);
            await loadAllPromise;
            await this.loadOne({ id, reFetch: true });
        },
        async updateCurrentDashboard ({ id, property, name, saveDashboard: save = true }: { id: string; property: string; name: unknown; saveDashboard?: boolean }) {
            await loadAllPromise;
            this.setDashboardProperty({ id, propertyKey: property, propertyValue: name });
            if (save) {
                this.saveDashboard(this.map[id] as Record<string, unknown>);
            }
        },
        async updateCurrentDashboardModules ({ id, modules = [] }: { id: string; modules?: unknown[] } = { id: '' }) {
            await loadAllPromise;
            this.setDashboardModules({ id, modules });
            await this.saveDashboard(this.map[id] as Record<string, unknown>);
        },
        async addWidgetsToDashboard ({ id, widgets, skipRefetch = false, position = 0 }: { id: string; widgets: unknown[]; skipRefetch?: boolean; position?: number }) {
            if (!skipRefetch) {
                await this.loadOne({ id, reFetch: true });
            }
            if ((this.map[id] as Record<string, unknown>)?.options) {
                delete (this.map[id] as Record<string, unknown>).options;
            }

            const completeWidgets: unknown[] = [];
            widgets.forEach((widget) => {
                const domSettings = (widgetDomSettings as Record<string, unknown>)[(widget as Record<string, unknown>).type as string];
                const { height, width } = (domSettings || {}) as { height?: unknown; width?: unknown };
                const completeWidget = {
                    id: uuid(),
                    ...(height ? { height } : {}),
                    ...(width ? { width } : {}),
                    ...(widget as Record<string, unknown>),
                    definition: {
                        layout: { column: 1 },
                        ...((widget as Record<string, unknown>).definition as Record<string, unknown>),
                    },
                };
                completeWidgets.push(completeWidget);
            });
            await loadAllPromise;
            this.addDashboardWidgets({ id, newWidgets: completeWidgets, position });
            await this.saveDashboard(this.map[id] as Record<string, unknown>);
        },
        async loadDashboardHistory () {
            const stored = ((await PerSubscriptionUserStorage.get('dashboardHistory')) || {}) as { value?: DashboardHistoryItem[] };
            this.dashboardHistory = stored.value || [];
        },
        setDashboardHistory (history: DashboardHistoryItem[]) {
            this.dashboardHistory = history;
        },
        async updateDashboardHistory ({ id }: { id: string }) {
            await this.loadDashboardHistory();
            const dashboardHistory = this.dashboardHistory.filter((item) => item.id !== id);
            dashboardHistory.unshift({ id, timestamp: moment().valueOf() });
            await PerSubscriptionUserStorage.set('dashboardHistory', dashboardHistory);
            this.setDashboardHistory(dashboardHistory);
        },
        async updateDashboardWidget ({ dashboardId, widgetId, widget }: { dashboardId: string; widgetId: string; widget: unknown }) {
            this.setDashboardWidget({ dashboardId, widgetId, widget });
            await this.saveDashboard(this.map[dashboardId] as Record<string, unknown>);
        },
        async deleteWidget ({ id, deletedWidget }: { id: string; deletedWidget: Record<string, unknown> }) {
            this.removeDashboardWidget({ dashboardId: id, widgetId: deletedWidget.id as string });
            await this.saveDashboard(this.map[id] as Record<string, unknown>);
        },
        async saveDashboard (dashboard: Record<string, unknown>) {
            delete dashboard.isNew;

            return updateDashboard(dashboard);
        },
        closePesDrilldown () {
            if (this.pesDrilldown.opened) {
                this.setPesDrilldown({ opened: false, widgetId: null });
            }
        },
        openPesDrilldown (options: Record<string, unknown>) {
            this.setPesDrilldown({ ...options, opened: true });
        },
    },
    getters: {
        dashboardById: (state) => (id: string) => state.map[id],
        list: (state): unknown[] => values(state.map),
        miniList: (state): Record<string, unknown>[] => {
            return values(state.map).map((dash) => {
                return Object.freeze(
                    pick(dash as Record<string, unknown>, [
                        'id', 'rootVersionId', 'stableVersionId', 'name', 'description', 'kind', 'owner',
                        'lastUpdatedByUser', 'lastUpdatedAt', 'createdAt', 'createdByUser', 'userDetails',
                        'userSharing', 'subscriptionSharing', 'isShared',
                    ]),
                ) as Record<string, unknown>;
            });
        },
        ownerDashboards (): Record<string, unknown>[] {
            return this.miniList.filter((dashboard) => ((dashboard.userDetails as Record<string, unknown>))?.permission === 'owner');
        },
        sharedDashboards (): Record<string, unknown>[] {
            return this.miniList.filter(
                (dashboard) => (dashboard.userDetails as Record<string, unknown>)?.permission !== 'owner' && (dashboard.userDetails as Record<string, unknown>)?.sharedWithMe,
            );
        },
        firstDashboard (): Record<string, unknown> | null {
            return this.ownerDashboards.length
                ? this.ownerDashboards.reduce((prev, curr) => {
                    return (prev.createdAt as number) < (curr.createdAt as number) ? prev : curr;
                })
                : null;
        },
        filtersByDashboardId: (state) => (dashboardId: string) => {
            return (
                state.dashboardFilterMap?.[dashboardId] || {
                    selections: {},
                    activeFilterOrder: [],
                }
            );
        },
        editableDashboards (): unknown[] {
            return this.list.filter(
                (dashboard) =>
                    ((dashboard as Record<string, unknown>).userDetails as Record<string, unknown>).permission === 'owner' ||
                    ((dashboard as Record<string, unknown>).userDetails as Record<string, unknown>).permission === 'edit',
            );
        },
        pesDrilldownFilters: (state) => ({
            account: state.pesDrilldown.account,
            appId: state.pesDrilldown.appId,
            segmentId: state.pesDrilldown.segmentId,
            timeSeries: state.pesDrilldown.timeSeries,
        }),
        pesDrilldownKind: (state) => ({ type: state.pesDrilldown.type, score: state.pesDrilldown.score }),
        pesDrilldownOpened: (state) => state.pesDrilldown.opened,
        pesDrilldownWidgetId: (state) => state.pesDrilldown.widgetId,
        validateWidgetAccess: () => (definition: unknown) => {
            // eslint-disable-next-line @typescript-eslint/no-require-imports
            const { useAppsStore } = require('@/stores/apps.store');

            return validateWidgetAccess(definition, {
                getAppById: (id: string) => useAppsStore().getAppById(id),
                hasViewerPermission: useSubscriptionStore().getFeatureFlag('enableViewerPermission'),
            });
        },
    },
});

export function __resetLoadAllPromiseForUnitTests () {
    loadAllPromise = null;
}

if (module.hot) {
    module.hot.accept(acceptHMRUpdate(useDashboardsStore, module.hot));
}
